import type { OpenPencilFigmaSourceBundle } from "./contracts.ts";

export interface SourceGenerationAdmissionResult {
  admitted: boolean;
  code:
    | "no_active_source"
    | "source_not_ready"
    | "source_not_admitted"
    | "admitted";
  message: string;
  requiredGateIds: string[];
}

export function evaluateOpenPencilGenerationAdmission(
  bundle: OpenPencilFigmaSourceBundle | null | undefined,
): SourceGenerationAdmissionResult {
  if (!bundle) {
    return {
      admitted: true,
      code: "no_active_source",
      message: "No source-authority bundle is active.",
      requiredGateIds: [],
    };
  }
  if (bundle.status !== "ready") {
    return {
      admitted: false,
      code: "source_not_ready",
      message: "The active source readback is blocked.",
      requiredGateIds: bundle.generationAdmission.requiredGateIds,
    };
  }
  if (bundle.generationAdmission.status !== "admitted") {
    return {
      admitted: false,
      code: "source_not_admitted",
      message:
        "The active source is verified, but page mapping, visual judgement, assembly, and runtime proof have not admitted generation.",
      requiredGateIds: bundle.generationAdmission.requiredGateIds,
    };
  }
  return {
    admitted: true,
    code: "admitted",
    message: "The active source passed generation admission.",
    requiredGateIds: [],
  };
}

export class SourceGenerationAdmissionError extends Error {
  readonly code: SourceGenerationAdmissionResult["code"];
  readonly requiredGateIds: string[];

  constructor(result: SourceGenerationAdmissionResult) {
    super(result.message);
    this.name = "SourceGenerationAdmissionError";
    this.code = result.code;
    this.requiredGateIds = result.requiredGateIds;
  }
}

export function assertOpenPencilGenerationAdmitted(
  bundle: OpenPencilFigmaSourceBundle | null | undefined,
): void {
  const result = evaluateOpenPencilGenerationAdmission(bundle);
  if (!result.admitted) throw new SourceGenerationAdmissionError(result);
}
